import { Location } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, Input, OnInit } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { BaseComponent, ToastService } from '@isophi/core-ui';
import {
  BlankEnum,
  ChildProfileDetail,
  ChildrenService,
  ChildSpzRecommendationUpsert,
  NullEnum,
  SupportingMeasures,
  SupportingMeasuresService,
} from '@isophi/mng-api';
import { TranslocoService } from '@jsverse/transloco';
import { NgxSpinnerService } from 'ngx-spinner';
import { map, Observable, takeUntil } from 'rxjs';

import { HelperService } from '../../../../core/services/helper.service';
import { ChildSpzRecommendationForm, ChildSpzRecommendationsForm, ChildSupportingMeasureForm } from '../../constants/forms';

@Component({
  selector: 'app-edit-child-spz-recommendations',
  templateUrl: './edit-child-spz-recommendations.component.html',
  styleUrls: ['./edit-child-spz-recommendations.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class EditChildSpzRecommendationsComponent extends BaseComponent implements OnInit {
  @Input() childId: string;

  protected child: ChildProfileDetail;
  protected savingRecommendations = false;

  private readonly route = inject(ActivatedRoute);
  private readonly childrenService = inject(ChildrenService);
  private readonly spinner = inject(NgxSpinnerService);
  private readonly toastService = inject(ToastService);
  private readonly translocoService = inject(TranslocoService);
  private readonly helperService = inject(HelperService);
  private readonly supportingMeasuresService = inject(SupportingMeasuresService);
  private readonly location = inject(Location);
  private readonly cd = inject(ChangeDetectorRef);

  protected form = new FormGroup<ChildSpzRecommendationsForm>({
    spzRecommendations: new FormArray<FormGroup<ChildSpzRecommendationForm>>([]),
  });

  protected supportingMeasureOptions$: Observable<SupportingMeasures[]> = this.supportingMeasuresService
    .supportingMeasuresList(1000)
    .pipe(map((supportingMeasures) => supportingMeasures.results));

  ngOnInit(): void {
    this.helperService.setTitle(location.pathname);
    this.childId = this.route.snapshot.params['id'];
    if (this.childId) {
      this.loadChild();
      this.loadRecommendations();
    }
  }

  get spzRecommendations(): FormArray<FormGroup<ChildSpzRecommendationForm>> {
    return this.form.get('spzRecommendations') as FormArray<FormGroup<ChildSpzRecommendationForm>>;
  }

  private loadChild(): void {
    this.spinner.show();
    this.childrenService
      .childrenRetrieve(this.childId)
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe({
        next: (childData: ChildProfileDetail) => {
          this.child = childData;
          this.helperService.smallTextTitle$.next(`${childData.firstName} ${childData.lastName}`);
        },
        error: () => {
          this.toastService.error(this.translocoService.translate('child/loadErrorToast'));
          this.spinner.hide();
        },
        complete: () => {
          this.spinner.hide();
        },
      });
  }

  private loadRecommendations(): void {
    this.spinner.show();
    this.childrenService
      .childrenChildSpzRecommendationList(this.childId)
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe({
        next: (recommendations) => {
          recommendations.forEach((recommendation) => {
            this.spzRecommendations.push(
              new FormGroup<ChildSpzRecommendationForm>({
                id: new FormControl(recommendation.id),
                spzRecommendationIssued: new FormControl(this.helperService.parseDate(recommendation.spzRecommendationIssued)),
                spzRecommendationDue: new FormControl(this.helperService.parseDate(recommendation.spzRecommendationDue)),
                izoNumber: new FormControl(recommendation.izoSpz),
                supportingMeasures: new FormArray<FormGroup<ChildSupportingMeasureForm>>(
                  recommendation.supportMeasures.map((supportingMeasure) => {
                    return new FormGroup<ChildSupportingMeasureForm>({
                      id: new FormControl(supportingMeasure.id),
                      supportingMeasure: new FormControl(supportingMeasure.supportingMeasures),
                      aidPurchaseForm: new FormControl(
                        supportingMeasure.aidPurchaseForm == BlankEnum.Empty || supportingMeasure.aidPurchaseForm == NullEnum.Null
                          ? null
                          : supportingMeasure.aidPurchaseForm
                      ),
                      isFundingRequired: new FormControl(supportingMeasure.isFundingRequired),
                      start: new FormControl(this.helperService.parseDate(supportingMeasure.supportingMeasuresStart)),
                      end: new FormControl(this.helperService.parseDate(supportingMeasure.supportingMeasuresEnd)),
                      actualProvisionStart: new FormControl(this.helperService.parseDate(supportingMeasure.actualProvisionStart)),
                      actualProvisionEnd: new FormControl(this.helperService.parseDate(supportingMeasure.actualProvisionEnd)),
                    });
                  })
                ),
              })
            );
          });
          this.cd.markForCheck();
        },
        error: () => {
          this.toastService.error(this.translocoService.translate('child/spzRecommendationsLoadErrorToast'));
          this.spinner.hide();
        },
        complete: () => {
          this.spinner.hide();
        },
      });
  }

  protected addRecommendation(): void {
    this.spzRecommendations.push(
      new FormGroup<ChildSpzRecommendationForm>({
        id: new FormControl(null),
        spzRecommendationIssued: new FormControl(null),
        spzRecommendationDue: new FormControl(null),
        izoNumber: new FormControl(null),
        supportingMeasures: new FormArray<FormGroup<ChildSupportingMeasureForm>>(
          [
            new FormGroup<ChildSupportingMeasureForm>({
              id: new FormControl(null),
              supportingMeasure: new FormControl(null),
              start: new FormControl(null),
              end: new FormControl(null),
              aidPurchaseForm: new FormControl(null),
              isFundingRequired: new FormControl(false),
              actualProvisionStart: new FormControl(null),
              actualProvisionEnd: new FormControl(null),
            }),
          ],
          { validators: Validators.minLength(1) }
        ),
      })
    );
    this.cd.markForCheck();
  }

  protected removeRecommendation(index: number): void {
    this.spzRecommendations.removeAt(index);
  }

  protected onSubmit(): void {
    const data: ChildSpzRecommendationUpsert[] = this.form.value.spzRecommendations.map((recommendation) => ({
      id: recommendation.id,
      spzRecommendationIssued: this.helperService.formateDate(recommendation.spzRecommendationIssued),
      spzRecommendationDue: this.helperService.formateDate(recommendation.spzRecommendationDue),
      izoSpz: recommendation.izoNumber,
      supportMeasures: recommendation.supportingMeasures.map((measure) => ({
        id: measure.id,
        supportingMeasures: measure.supportingMeasure,
        supportingMeasuresStart: this.helperService.formateDate(measure.start),
        supportingMeasuresEnd: this.helperService.formateDate(measure.end),
        aidPurchaseForm: measure.aidPurchaseForm ?? BlankEnum.Empty,
        isFundingRequired: measure.isFundingRequired,
        actualProvisionEnd: this.helperService.formateDate(measure.actualProvisionEnd),
        actualProvisionStart: this.helperService.formateDate(measure.actualProvisionStart),
      })),
    }));
    this.savingRecommendations = true;
    this.spinner.show();
    this.childrenService
      .childrenChildSpzRecommendationBulkUpsertCreate(data, this.childId)
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe({
        next: () => {
          this.toastService.success(this.translocoService.translate('child/spzRecommendationsSaveSuccessToast'));
          this.location.back();
        },
        error: () => {
          this.toastService.error(this.translocoService.translate('child/spzRecommendationsSaveErrorToast'));
          this.savingRecommendations = false;
          this.spinner.hide();
        },
        complete: () => {
          this.savingRecommendations = false;
          this.spinner.hide();
        },
      });
  }

  protected onCancel(): void {
    this.location.back();
  }
}
